import { Link } from "@tanstack/react-router";
import {
  EllipsisVertical,
  LayoutTemplate,
  Library,
  Pencil,
  SearchX,
  Trash2,
} from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Template } from "@/common/types/templates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  EmptyState,
  LIST_CARD,
  LoadErrorBanner,
  SearchField,
  TOUCH_BUTTON,
  TOUCH_ICON_BUTTON,
} from "@/components/ui/redesign/page";
import { Skeleton } from "@/components/ui/skeleton";
import { TemplateGroups } from "@/components/ui/templates/template-groups";
import { useTemplateDelete } from "@/components/ui/templates/template-item";
import {
  type TemplateChoice,
  templateChoices,
} from "@/lib/template-preferences";
import { cn } from "@/lib/utils";

const normalize = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const EMPTY_TEMPLATE: Template = {
  public_id: "",
  name: "",
  headers: [],
  createDate: "",
  isDefault: 0,
};

// Name and section headings, case- and accent-insensitive.
export const matchesTemplate = (template: Template, query: string): boolean => {
  const needle = normalize(query);
  if (!needle) return true;
  const haystack = normalize(
    [template.name, ...(template.headers ?? [])].join(" "),
  );
  return needle.split(/\s+/).every((word) => haystack.includes(word));
};

// "…" with Edit and Delete for one of your templates.
const TemplateRowMenu = ({ template }: { template: Template }): JSX.Element => {
  const { t } = useTranslation();
  const { remove, loading } = useTemplateDelete();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="shrink-0">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label={t("templates.home.moreActions", {
              name: template.name,
            })}
            className={cn(TOUCH_ICON_BUTTON, "text-v3-text-3")}
            size="icon"
            type="button"
            variant="ghost"
          >
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="min-w-48 **:[[role=menuitem]]:min-h-11 lg:**:[[role=menuitem]]:min-h-9"
        >
          <DropdownMenuItem asChild className="gap-3">
            <Link
              params={{ templatePublicId: String(template.public_id) }}
              to="/template/$templatePublicId"
            >
              <Pencil aria-hidden className="size-4" />
              {t("templates.home.edit")}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="gap-3 text-error focus:text-error"
            disabled={loading}
            onSelect={() => {
              setConfirmOpen(true);
            }}
          >
            <Trash2 aria-hidden className="size-4" />
            {t("templates.home.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("templates.deleteTemplateAlert.headerTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("templates.deleteTemplateAlert.deleteCTA")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={TOUCH_BUTTON}>
              {t("templates.deleteTemplateAlert.cancelButton")}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                TOUCH_BUTTON,
                "bg-destructive text-destructive-foreground hover:bg-destructive/80",
              )}
              disabled={loading}
              onClick={() => {
                void remove(template.public_id);
              }}
            >
              {t("templates.deleteTemplateAlert.deleteButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const LoadingList = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div
      aria-busy
      aria-label={t("templates.home.loading")}
      className={LIST_CARD}
      data-testid="templates-loading"
      role="status"
    >
      {[0, 1, 2, 3].map((index) => (
        <div
          key={index}
          aria-hidden
          className="flex min-h-[72px] items-center gap-4 border-b border-v3-border px-4 last:border-b-0 sm:px-5"
        >
          <Skeleton className="size-10 rounded-lg" role="presentation" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/2 max-w-xs" role="presentation" />
            <Skeleton className="h-3 w-24" role="presentation" />
          </div>
        </div>
      ))}
    </div>
  );
};

interface Props {
  templates: Template[];
  loading: boolean;
  error: boolean;
  ready: boolean;
  onRetry: () => void;
  onBrowseLibrary: () => void;
  newTemplateButton: React.ReactNode;
}

// "My templates": search, then one card of templates (name, Default, its
// sections); a row opens the editor and "…" holds Edit and Delete.
export const MyTemplates = ({
  templates,
  loading,
  error,
  ready,
  onRetry,
  onBrowseLibrary,
  newTemplateButton,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const matches = (choice: TemplateChoice): boolean =>
    choice.template
      ? matchesTemplate(choice.template, query)
      : matchesTemplate({ ...EMPTY_TEMPLATE, name: choice.name }, query);
  const shown = templateChoices(templates).filter(matches);
  const hasList = templates.length > 0;
  const empty = (
    <EmptyState
      className="rounded-2xl bg-v3-panel shadow-v3-hairline-strong px-5 sm:px-10"
      description={t("templates.home.emptyDescription")}
      icon={LayoutTemplate}
      testId="templates-empty"
      title={t("templates.emptyStateCTA")}
    >
      {newTemplateButton}
      <Button
        className={cn(TOUCH_BUTTON, "gap-1.5")}
        type="button"
        variant="outline"
        onClick={onBrowseLibrary}
      >
        <Library aria-hidden />
        {t("templates.home.browseLibrary")}
      </Button>
    </EmptyState>
  );

  return (
    <div className="space-y-4" data-testid="my-templates">
      {error && (
        <LoadErrorBanner
          description={
            hasList
              ? t("templates.home.loadError.stale")
              : t("templates.home.loadError.empty")
          }
          title={t("error.unknown")}
          onRetry={onRetry}
        />
      )}
      {loading && !hasList && !error && <LoadingList />}
      {ready && !hasList && !error && (
        <TemplateGroups
          actionsClassName="w-11 lg:w-9"
          emptyYours={empty}
          renderActions={() => null}
          templates={[]}
        />
      )}
      {hasList && (
        <>
          {templates.length > 5 && (
            <SearchField
              className="lg:max-w-80"
              clearLabel={t("templates.home.clearSearch")}
              label={t("templates.home.search")}
              testId="templates-search"
              value={query}
              onChange={setQuery}
            />
          )}
          {shown.length === 0 ? (
            <EmptyState
              description={t("templates.home.noResults.description")}
              icon={SearchX}
              testId="templates-no-results"
              title={t("templates.home.noResults.title")}
            >
              <Button
                className={TOUCH_BUTTON}
                type="button"
                variant="outline"
                onClick={() => {
                  setQuery("");
                }}
              >
                {t("templates.home.noResults.reset")}
              </Button>
            </EmptyState>
          ) : (
            <TemplateGroups
              actionsClassName="w-11 lg:w-9"
              matches={matches}
              renderActions={(template) => (
                <TemplateRowMenu template={template} />
              )}
              templates={templates}
            />
          )}
        </>
      )}
    </div>
  );
};
